import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { labelFontStyle, badgeStyle } from '../../styles/typography'
import type { Category, Subcategory } from '../../types/classification'

interface CategorySelectorProps {
  selectedCategoryId: string | null
  selectedSubcategoryId: string | null
  onCategoryChange: (categoryId: string | null) => void
  onSubcategoryChange: (subcategoryId: string | null) => void
  categoryError?: boolean
  isEditable?: boolean
  categoryName?: string // For view mode display
  subcategoryName?: string // For view mode display
  className?: string
}

export function CategorySelector({
  selectedCategoryId,
  selectedSubcategoryId,
  onCategoryChange,
  onSubcategoryChange,
  categoryError = false,
  isEditable = true,
  categoryName,
  subcategoryName,
  className = ''
}: CategorySelectorProps) {
  const [categories, setCategories] = useState<Category[]>([])
  const [subcategories, setSubcategories] = useState<Subcategory[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Load categories and subcategories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setIsLoading(true)

        const [categoriesRes, subcategoriesRes] = await Promise.all([
          supabase.from('categories').select('id, name').order('name'),
          supabase.from('subcategories').select('id, name, category_id').order('name')
        ])

        if (categoriesRes.data) setCategories(categoriesRes.data)
        if (subcategoriesRes.data) setSubcategories(subcategoriesRes.data)
      } catch (error) {
        console.error('Error loading categories:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadCategories()
  }, [])

  // Filter subcategories based on selected category
  const filteredSubcategories = selectedCategoryId
    ? subcategories.filter(sub => sub.category_id === selectedCategoryId)
    : subcategories

  // View mode
  if (!isEditable) {
    return (
      <div className={`grid grid-cols-2 gap-x-8 ${className}`}>
        {/* Category */}
        <div>
          <Label style={labelFontStyle}>Category</Label>
          {categoryName ? (
            <Badge variant="secondary" className="mt-1" style={badgeStyle}>
              {categoryName}
            </Badge>
          ) : (
            <p className="text-gray-400 text-sm mt-1" style={labelFontStyle}>
              Not categorized
            </p>
          )}
        </div>

        {/* Subcategory */}
        <div>
          <Label style={labelFontStyle}>Subcategory</Label>
          {subcategoryName ? (
            <Badge variant="outline" className="mt-1" style={badgeStyle}>
              {subcategoryName}
            </Badge>
          ) : (
            <p className="text-gray-400 text-sm mt-1" style={labelFontStyle}>
              —
            </p>
          )}
        </div>
      </div>
    )
  }

  // Edit mode
  return (
    <div className={`grid grid-cols-2 gap-x-8 ${className}`}>
      {/* Category */}
      <div>
        <Label style={labelFontStyle}>
          Category <span className="text-red-500">*</span>
        </Label>
        <Select
          value={selectedCategoryId || ''}
          onValueChange={(value) => {
            onCategoryChange(value || null)
            // Clear subcategory when category changes
            if (value !== selectedCategoryId) {
              onSubcategoryChange(null)
            }
          }}
          disabled={isLoading}
        >
          <SelectTrigger
            className={`mt-1 ${categoryError ? 'border-red-500 focus:border-red-500' : ''}`}
          >
            <SelectValue placeholder={isLoading ? 'Loading...' : 'Select category...'} />
          </SelectTrigger>
          <SelectContent className="max-h-[300px] overflow-y-auto">
            {categories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Subcategory */}
      <div>
        <Label style={labelFontStyle}>Subcategory</Label>
        <Select
          value={selectedSubcategoryId || ''}
          onValueChange={(value) => onSubcategoryChange(value === '__none__' ? null : value || null)}
          disabled={isLoading || !selectedCategoryId}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder={
              isLoading ? 'Loading...' :
              !selectedCategoryId ? 'Select category first...' :
              'Select subcategory (optional)...'
            } />
          </SelectTrigger>
          <SelectContent className="max-h-[300px] overflow-y-auto">
            <SelectItem value="__none__">None</SelectItem>
            {filteredSubcategories.map((subcategory) => (
              <SelectItem key={subcategory.id} value={subcategory.id}>
                {subcategory.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

// Export helper function to get category/subcategory names from IDs
export function useCategoryNames(
  categoryId: string | null,
  subcategoryId: string | null
): { categoryName: string | null; subcategoryName: string | null } {
  const [names, setNames] = useState<{ categoryName: string | null; subcategoryName: string | null }>({
    categoryName: null,
    subcategoryName: null
  })

  useEffect(() => {
    const loadNames = async () => {
      if (!categoryId && !subcategoryId) {
        setNames({ categoryName: null, subcategoryName: null })
        return
      }

      try {
        // Parallel queries for category and subcategory names
        const [categoryResult, subcategoryResult] = await Promise.all([
          categoryId
            ? supabase.from('categories').select('name').eq('id', categoryId).single()
            : Promise.resolve({ data: null }),
          subcategoryId
            ? supabase.from('subcategories').select('name').eq('id', subcategoryId).single()
            : Promise.resolve({ data: null })
        ])

        setNames({
          categoryName: categoryResult.data?.name || null,
          subcategoryName: subcategoryResult.data?.name || null
        })
      } catch (error) {
        console.error('Error loading category names:', error)
      }
    }

    loadNames()
  }, [categoryId, subcategoryId])

  return names
}
